/**
 * 카메라 화면 흔들림 (FR-007/017, 원칙 III).
 * 임팩트 강도에 비례한 진폭 + 감쇠. Settings.shakeIntensity(0=끄기)를 곱한다.
 */
import * as THREE from 'three';
import type { EventBus } from '../core/EventBus';

export class ScreenShake {
  private trauma = 0; // 0..1
  private intensity = 1.0; // Settings에서 주입 (0 = 끄기)
  private readonly maxOffset = 0.22;
  private readonly maxRoll = 0.06;
  private seed = Math.random() * 1000;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private basePos: THREE.Vector3,
    bus: EventBus,
  ) {
    bus.on('object:broken', (e) => this.addTrauma(0.35 + Math.min(0.5, e.impactSpeed / 30)));
    // 비파괴 충격(책상에 쿵)에도 미세한 흔들림 — 무게감.
    bus.on('object:impact', (e) => this.addTrauma(Math.min(0.16, e.relSpeed / 40)));
    // 책상 반파/뒤집기 — 최대 트라우마(게임에서 가장 큰 임팩트).
    bus.on('desk:destroyed', () => this.addTrauma(1.0));
    // 쓸어버리기 — 팔을 크게 휘두른 반동(뒤따르는 파괴 셰이크가 겹쳐 커진다).
    bus.on('rage:sweep', () => this.addTrauma(0.45));
    bus.on('settings:changed', (s) => (this.intensity = s.shakeIntensity));
  }

  addTrauma(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /** 흔들림 진행 중 여부 — 유휴 렌더 게이팅의 활동 신호(main.ts 배선). */
  get active(): boolean {
    return this.trauma > 0;
  }

  /** 매 프레임(가변 dt). 카메라 위치/회전에 흔들림 적용 후 감쇠. */
  update(dt: number): void {
    // 기본 위치로 복귀.
    this.camera.position.copy(this.basePos);
    this.camera.rotation.z = 0;

    const power = this.trauma * this.trauma * this.intensity;
    if (power > 0.0001) {
      const t = (this.seed += dt * 40);
      const nx = Math.sin(t * 1.7) * Math.cos(t * 0.9);
      const ny = Math.sin(t * 2.3 + 1.3) * Math.cos(t * 1.1);
      this.camera.position.x = this.basePos.x + nx * this.maxOffset * power;
      this.camera.position.y = this.basePos.y + ny * this.maxOffset * power;
      this.camera.rotation.z = Math.sin(t * 2.9) * this.maxRoll * power;
    }
    // 감쇠: trauma를 초당 1.6씩 선형 감소 → 최대치(1.0)에서 약 0.6초 만에 소멸.
    this.trauma = Math.max(0, this.trauma - dt * 1.6);
  }
}
